import React, { useEffect, useMemo, useState } from 'react';

import { CloseOutlined, FileOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Steps, Upload } from 'antd';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { NotificationLink, PlanningLink } from 'components/Links';
import { Modal } from 'components/Modal';
import {
  BulkImportModalProps,
  BulkImportName,
  BulkImportRequestBase,
  downloadHeaderTemplate,
  parseUploadedData,
  validateItemLocationRows,
} from 'components/recipes/BulkImportModal/BulkImportDropdown/utils';
import { Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { routes } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { PersistedColumn } from 'utils/tableAndSidePaneSettings/types';

import { CenteredLoader } from '../../Loaders';
import { CollapsePanels } from '../sidePane/CollapsePanels';

export enum UploadStep {
  Upload,
  Review,
}

function getBulkImportEntityCopy(name: string) {
  if (name === BulkImportName.ItemInheritance) {
    return {
      invalidAlert: 'item pairs',
      invalidTooltip: 'Item pairs',
      validTooltip: 'Item pairs',
    };
  }
  if (name === BulkImportName.MaxShelfLife) {
    return {
      invalidAlert: 'item(s)',
      invalidTooltip: 'Items',
      validTooltip: 'Items',
    };
  }
  return {
    invalidAlert: 'item/location(s)',
    invalidTooltip: 'Item/location pairs',
    validTooltip: 'Item/location pairs',
  };
}

export function BulkImportModal<BulkImportRequest extends BulkImportRequestBase>({
  onClose,
  inputs,
  onUpdate,
  name,
  onValidate = validateItemLocationRows as BulkImportModalProps<BulkImportRequest>['onValidate'],
}: BulkImportModalProps<BulkImportRequest>) {
  const [currentStep, setCurrentStep] = useState<UploadStep>(UploadStep.Upload);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [validItemLocations, setValidItemLocations] = useState<BulkImportRequest[]>([]);
  const [invalidItemLocations, setInvalidItemLocations] = useState<BulkImportRequest[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isItemInheritanceUpdate = name === BulkImportName.ItemInheritance;
  const isSupplierLocationUpdate = [
    BulkImportName.SupplierTargets,
    BulkImportName.SupplierMinimums,
    BulkImportName.SupplierReviewCycle,
    BulkImportName.SupplierOrderCycle,
  ].includes(name);
  const entityCopy = getBulkImportEntityCopy(name);
  const { activeTenant } = useGlobalApp();

  const validatingOrLoading = isValidating || isUploading;
  const primaryKeys = useMemo(() => inputs.filter((input) => input.isPrimaryKey).map((input) => input.name), [inputs]);
  const columnNames: (keyof BulkImportRequest)[] = useMemo(
    () => inputs.map((input) => input.name) as (keyof BulkImportRequest)[],
    [inputs],
  );

  const getSupplierLocationColumns = (
    isApiValidated = false,
  ): PersistedColumn<BulkImportRequest & { itemId: string | undefined }>[] =>
    [
      {
        title: 'Supplier ID',
        dataIndex: 'supplierId',
        settingKey: 'supplierId',
        render: (value: string) => String(value ?? '-'),
      },
      isApiValidated
        ? {
            title: 'Supplier Name',
            dataIndex: 'supplierName',
            settingKey: 'supplierName',
            render: (value: string) => String(value ?? '-'),
          }
        : null,
      {
        title: 'Location ID',
        dataIndex: 'locationId',
        settingKey: 'locationId',
        render: (value: string) => String(value ?? '-'),
      },
      isApiValidated
        ? {
            title: 'Location Name',
            dataIndex: 'locationName',
            settingKey: 'locationName',
            render: (value: string) => String(value ?? '-'),
          }
        : null,
      ...inputs
        .filter((input) => !input.isPrimaryKey)
        .map((input) => ({
          title: input.name,
          dataIndex: input.name,
          settingKey: input.name,
          render: (value: string) => String(value ?? '-'),
        })),
    ].filter(truthy) as PersistedColumn<BulkImportRequest & { itemId: string | undefined }>[];

  const getItemInheritanceColumns = (
    isApiValidated = false,
  ): PersistedColumn<BulkImportRequest & { itemId: string | undefined }>[] =>
    [
      isApiValidated
        ? {
            title: 'Ancestor Item ID',
            dataIndex: 'ancestorItemCode',
            settingKey: 'ancestorItemCode',
            render: (value: string, _record: BulkImportRequest & { itemId: string | undefined }) => (
              <PlanningLink
                to={routes.demandPlanning.planning({
                  query: value ?? '',
                })}
                target="_blank"
              >
                {value}
              </PlanningLink>
            ),
          }
        : null,
      {
        title: 'Ancestor Item UID',
        dataIndex: 'ancestorItemUid',
        settingKey: 'ancestorItemUid',
        render: (value: string) => String(value ?? '-'),
      },
      isApiValidated
        ? {
            title: 'Descendant Item ID',
            dataIndex: 'descendantItemCode',
            settingKey: 'descendantItemCode',
            render: (value: string, _record: BulkImportRequest & { itemId: string | undefined }) => (
              <PlanningLink
                to={routes.demandPlanning.planning({
                  query: value ?? '',
                })}
                target="_blank"
              >
                {value}
              </PlanningLink>
            ),
          }
        : null,
      {
        title: 'Descendant Item UID',
        dataIndex: 'descendantItemUid',
        settingKey: 'descendantItemUid',
        render: (value: string) => String(value ?? '-'),
      },
    ].filter(truthy);

  const getColumns = (isApiValidated = false): PersistedColumn<BulkImportRequest & { itemId: string | undefined }>[] =>
    isSupplierLocationUpdate
      ? getSupplierLocationColumns(isApiValidated)
      : isItemInheritanceUpdate
      ? getItemInheritanceColumns(isApiValidated)
      : [
          isApiValidated
            ? {
                title: 'Item ID',
                dataIndex: 'itemId',
                settingKey: 'itemId',
                render: (value: string, record: BulkImportRequest & { itemId: string | undefined }) => (
                  <PlanningLink
                    to={routes.demandPlanning.planning({
                      query: record.itemId ?? '',
                    })}
                    target="_blank"
                  >
                    {value}
                  </PlanningLink>
                ),
              }
            : null,
          ...inputs.map((input) => ({
            title: input.name,
            dataIndex: input.name,
            settingKey: input.name,
            render: (value: string) => String(value ?? '-'),
          })),
        ].filter(truthy);
  useEffect(() => {
    if (error !== null) {
      setIsUploading(false);
      setIsValidating(false);
      setCurrentStep(UploadStep.Upload);
    }
  }, [error]);

  const removeFile = () => {
    setFileName('');
    setError(null);
  };

  return (
    <Modal
      title={`Upload ${name} for ${activeTenant.name}`}
      visible
      onCancel={() => onClose()}
      centered
      width={1200}
      bodyStyle={currentStep === UploadStep.Review ? { height: '60vh', overflowY: 'scroll' } : {}}
      footer={
        <>
          <Button onClick={() => setCurrentStep(UploadStep.Upload)} disabled={currentStep === UploadStep.Upload}>
            Previous
          </Button>
          <Button
            type="primary"
            disabled={
              fileName === '' ||
              error !== null ||
              (currentStep === UploadStep.Review && validItemLocations.length === 0)
            }
            loading={isSubmitting}
            onClick={async () => {
              if (currentStep === UploadStep.Upload) {
                setCurrentStep(UploadStep.Review);
              } else if (currentStep === UploadStep.Review) {
                setIsSubmitting(true);
                const submitNotification = createSubmissionNotification({
                  entityName: 'Bulk Upload',
                  // 30 sec + 30 sec per 1k updates
                  expectedWaitSeconds: 30 + Math.round(validItemLocations.length / 1_000) * 30,
                });
                try {
                  const submitColumns = [...columnNames];
                  if (name === BulkImportName.MinMax) {
                    submitColumns.push('itemId' as keyof BulkImportRequest);
                  }
                  const validatedData = validItemLocations.map((item) => {
                    const filtered = {} as BulkImportRequest;
                    for (const column of submitColumns) {
                      filtered[column] = item[column];
                    }
                    return filtered;
                  });
                  const { data: responseData } = await onUpdate(validatedData);
                  submitNotification.success({
                    successMessage: 'CSV Upload completed',
                    description: (successNotificationKey) =>
                      responseData?.jobId ? (
                        <NotificationLink
                          notificationKey={successNotificationKey}
                          to={routes.monitoring.exportJobDetails(responseData.jobId)}
                        >
                          View Export Details
                        </NotificationLink>
                      ) : null,
                  });
                  onClose();
                } catch (err) {
                  submitNotification.error(err);
                }
                setIsSubmitting(false);
              }
            }}
          >
            {currentStep === UploadStep.Review ? `Save Updates for ${activeTenant.name}` : 'Next'}
          </Button>
        </>
      }
    >
      <Steps current={currentStep}>
        <Steps.Step title={`Upload ${name}`} />
        <Steps.Step title="Review" />
      </Steps>
      {currentStep === UploadStep.Upload && (
        <Container>
          <FlexSpace direction="column" gap={16} alignItems="center">
            <FlexSpacer />
            Upload a CSV to update configurations in bulk. Each type has its own template, so download and review it
            before adding your list of ids and changes and uploading. Your file will be validated before any changes are
            applied.
            {[BulkImportName.SupplierTargets, BulkImportName.SupplierMinimums].includes(name) &&
              ' To clear an existing target, pass in a value of 0 or leave the value column empty.'}
            {name === BulkImportName.MaxShelfLife &&
              ' Provide itemUid or itemCode (at least one). Leave maxShelfLifeDays empty or pass 0 to clear.'}
            <Button
              onClick={() =>
                downloadHeaderTemplate(
                  // Header values from inputs
                  inputs.map((val) => val.name),
                  name,
                )
              }
            >
              Download Template
            </Button>
            {fileName === '' ? (
              <Upload
                accept=".csv"
                maxCount={1}
                showUploadList={false}
                beforeUpload={async (file) => {
                  await parseUploadedData<BulkImportRequest>(
                    file,
                    inputs,
                    async (rowList, dataByKey) => {
                      setIsValidating(true);
                      try {
                        const { valid, invalid } = await onValidate!(rowList, dataByKey);
                        setValidItemLocations(valid);
                        setInvalidItemLocations(invalid);
                      } catch (err) {
                        setValidItemLocations([]);
                        setInvalidItemLocations([]);
                        setError('Failed to validate uploaded file. Please try again.');
                      } finally {
                        setIsValidating(false);
                      }
                    },
                    setIsUploading,
                    setError,
                    setFileName,
                  );
                }}
              >
                <Button type="primary" loading={validatingOrLoading}>
                  Upload Data
                </Button>
              </Upload>
            ) : (
              <FlexSpace gap={8} justify="left" alignItems="center">
                <Tooltip title={fileName}>
                  <FileOutlined />
                </Tooltip>
                <p
                  className={css`
                    margin-top: auto;
                    margin-bottom: auto;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 250px;
                  `}
                >
                  {fileName}
                </p>
                <ActionButton label={<CloseOutlined />} onClick={() => removeFile()} />
              </FlexSpace>
            )}
            <div
              className={css`
                margin-left: auto;
                margin-right: auto;
                color: red;
              `}
            >
              {error}
            </div>
          </FlexSpace>
        </Container>
      )}
      {currentStep === UploadStep.Review && (
        <Container
          className={css`
            display: flex;
            flex-direction: column;
            gap: 32px;
            margin: 0 16px 0 16px;
            paddding: 0;
          `}
        >
          {name === BulkImportName.ExcludeInclude && (
            <Alert
              className={css`
                margin-top: 10px;
              `}
              message="Item-locations excluded through Rules must be re-included through the specific Rule"
              banner
              type="info"
              onClose={() => {}}
            />
          )}
          {invalidItemLocations.length > 0 && (
            <Alert
              message={`${invalidItemLocations.length} item(s) in the CSV could not be validated. These ${entityCopy.invalidAlert} will not be updated.`}
              type="error"
              className={css`
                margin-top: 24px;
              `}
              banner
            />
          )}
          {validatingOrLoading ? (
            <CenteredLoader />
          ) : (
            <CollapsePanels
              panels={[
                invalidItemLocations.length > 0
                  ? {
                      tooltip: `${entityCopy.invalidTooltip} that could not be validated and will not be updated.`,
                      title: (
                        <FlexSpace direction="row" gap={16} alignItems="center" justify="flex-start">
                          <span
                            className={css`
                              flex: 0 0 auto;
                            `}
                          >
                            Invalid Items ({invalidItemLocations.length})
                          </span>
                        </FlexSpace>
                      ),
                      content: (
                        <Table
                          key="invalid-items"
                          pageSize={10}
                          isLoading={validatingOrLoading}
                          data={invalidItemLocations}
                          columns={getColumns(false)}
                          rowKey={(record) =>
                            primaryKeys.map((k) => String((record as Record<string, unknown>)[k])).join('|')
                          }
                          size="large"
                        />
                      ),
                      isCollapsedByDefault: true,
                    }
                  : null,
                {
                  title: `Validated Items (${validItemLocations.length})`,
                  tooltip: `${entityCopy.validTooltip} that will be updated. Please use the links below to confirm item selection.`,
                  isCollapsedByDefault: true,
                  content:
                    validItemLocations.length > 0 ? (
                      <Table
                        key="validated-items"
                        className={css`
                          margin-top: 16px;
                        `}
                        pageSize={10}
                        isLoading={validatingOrLoading}
                        data={validItemLocations}
                        columns={getColumns(true)}
                        rowKey={(record) =>
                          primaryKeys.map((k) => String((record as Record<string, unknown>)[k])).join('|')
                        }
                        size="small"
                      />
                    ) : (
                      <></>
                    ),
                },
              ]}
            />
          )}
        </Container>
      )}
    </Modal>
  );
}
